<template>
  <div v-if="show" class="modal fade show custom-modal-backdrop" aria-modal="true" role="dialog">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="row">
          <div class="col-12">
            <div class="d-flex justify-content-between px-1 my-1 align-items-center">
              <h1 class="mb-0">{{ modalTitle }}</h1>
              <div class="d-flex align-items-center gap-2">
                <button
                    @click="onCancel"
                    type="button"
                    class="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                ></button>
              </div>
            </div>
          </div>
        </div>
        <hr class="mt-0">
        <div class="modal-body pt-0">
          <div v-if="isCompleted" class="text-center mb-3">
            <p class="text-dark">Are you sure you want to revert <strong>{{ item.name }}</strong> to not completed?</p>
          </div>
          <div v-else class="text-center mb-3">
            <p class="text-dark">Are you sure you want to mark <strong>{{ item.name }}</strong> as completed?</p>
          </div>
          <div v-if="!isCompleted" class="form-group text-start">
            <label class="form-label fw-bold small text-uppercase text-dark">Completion Note</label>
            <textarea 
              class="form-control" 
              rows="3" 
              v-model="completionNote" 
              placeholder="Enter completion details..."
            ></textarea>
          </div>
        </div>
        <div class="modal-footer pt-0 border-top-0">
          <button type="button" class="btn btn-secondary" @click="onCancel">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="loading" @click="onSave">
            <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
            {{ submitLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, defineProps, defineEmits, watch } from 'vue';
import handleProjects from "@/services/modules/procurement/project";
import { useToast } from "vue-toastification";

const props = defineProps({
  show: Boolean,
  item: Object
});

const emit = defineEmits(['update:show', 'success']);
const { updateProjectAccountCompletion } = handleProjects();
const toast = useToast();

const completionNote = ref('');
const loading = ref(false);

const isCompleted = computed(() => props.item.completion_status === 'completed');

const modalTitle = computed(() => {
  if (isCompleted.value) return 'Revert Completion';
  return 'Completion';
});

const submitLabel = computed(() => {
  if (isCompleted.value) return 'Revert';
  return 'Save & Mark Done';
});

const onCancel = () => {
  completionNote.value = '';
  emit('update:show', false);
};

const buildPayload = () => {
  return {
    project_account_id: props.item.project_account_id,
    project_id: props.item.project_id,
    company_id: props.item.company_id,
    completion_note: completionNote.value,
    is_completed: ! isCompleted.value
  };
};

const onSave = async () => {
  if (loading.value) return;

  loading.value = true;
  const wasCompleted = isCompleted.value;
  try {
    const response = await updateProjectAccountCompletion(props.item.id, buildPayload(), '');

    if (!response.status) {
      toast.error(response.message || "Failed to update completion status.");
      return;
    }

    toast.success(response.message || "Completion status updated!");
    emit('success', buildResult(wasCompleted));
    onCancel();
  } catch (error) {
    toast.error("An error occurred while updating completion status.");
  } finally {
    loading.value = false;
  }
};

const buildResult = (wasCompleted) => {
  if (wasCompleted) return { status: null, note: null };
  return { status: 'completed', note: completionNote.value };
};

watch(() => props.show, (newVal) => {
  if (newVal) {
    completionNote.value = props.item.completion_note || '';
  }
});
</script>

<style scoped>
.custom-modal-backdrop {
  display: block;
  background-color: rgba(28, 29, 28, 0.2);
  z-index: 1060;
}

.modal-content {
  border-radius: 0.358rem;
}

h1 {
  font-size: 1.25rem;
}

.form-control {
  resize: none;
}
</style>
